import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import { format, addMinutes, startOfDay } from 'date-fns';
import classNames from 'classnames';

import { intlMessage } from 'constants/propTypes';

import Autocomplete from '../Autocomplete/Autocomplete';
import InputLabel from '../InputLabel/InputLabel';
import './TimePicker.scss';

const messages = defineMessages({
  selectStartTime: {
    id: 'components.form.time-picker.placeholder',
    defaultMessage: 'Select start time',
  },
});

export const HOURS_PER_DAY = 24;
export const INTERVAL_IN_MINUTES = 5;
export const INTERVALS_PER_HOUR = 60 / INTERVAL_IN_MINUTES;

const getDayTimes = intl => {
  let runningDate = startOfDay(new Date());
  const maxIterations = HOURS_PER_DAY * INTERVALS_PER_HOUR;
  const times = [];

  for (let i = 0; i < maxIterations; i += 1) {
    times.push({
      label: intl.formatDate(runningDate, { format: 'time' }),
      value: format(runningDate, 'HH:mm'),
    });
    runningDate = addMinutes(runningDate, INTERVAL_IN_MINUTES);
  }
  return times;
};

export const filterTime = ({ label }, filter) => {
  const transformRawString = str => {
    const scrubbed = str.replace(/:|\s/gi, '');
    return [scrubbed.replace(/\D/gi, '').replace(/^0/g, ''), scrubbed.replace(/\d/gi, '').toLowerCase()];
  };

  const [labelHour, labelTimeSuffix] = transformRawString(label);
  const [filterHour, filterTimeSuffix] = transformRawString(filter);

  if (!filterHour) return true;

  if (filterTimeSuffix && !labelTimeSuffix.startsWith(filterTimeSuffix)) return false;

  if (filterHour.length < 3 && Number(filterHour) < 24) {
    return labelHour.slice(0, -2) === filterHour;
  }

  return labelHour.startsWith(filterHour);
};

const TimePicker = ({
  options,
  className,
  label,
  placeholder = messages.selectStartTime,
  showErrorImmediately,
  ...restProps
}) => {
  const intl = useIntl();

  return (
    <div className={classNames('time-picker', className)} {...restProps}>
      <InputLabel message={label} />
      <Autocomplete
        {...restProps}
        options={options || getDayTimes(intl)}
        optionsFilter={filterTime}
        placeholder={placeholder}
        showErrorImmediately={showErrorImmediately}
      />
    </div>
  );
};

TimePicker.propTypes = {
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  placeholder: intlMessage,
  label: intlMessage,
  showErrorImmediately: PropTypes.bool,
};

export default TimePicker;
